import { useEffect, useRef, useState } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import type { UserPersona } from '@ddp/shared';

import { useAuthStore } from '../auth/store.js';
import { GuidedTour } from './GuidedTour.js';
import { PersonaModal } from './PersonaModal.js';
import { PERSONA_LANDING, TOURS, scopeForPath, tourIsPending } from './tours.js';
import { useTourStore } from './tour-store.js';

/**
 * Orchestrateur de l'onboarding (ADR-0031), monté dans la coquille applicative. Il décide, à un
 * instant donné, quelle visite guidée montrer :
 *
 *   1. **rejeu explicite** (bouton de section) — prioritaire, contourne « déjà vue » ;
 *   2. **modale persona** si le persona n'a jamais été choisi ;
 *   3. **visite `overview`** si elle est en attente (ancrée sur le rail, indépendante de la page) ;
 *   4. **visite de la section courante** (`data`/`model`/`deploy`) si elle est en attente — d'où le
 *      déclenchement d'une nouveauté quand un utilisateur installé arrive sur la page concernée.
 *
 * Une seule visite à la fois. Après fermeture, on la marque vue (idempotent) ; l'effet ré-évalue et
 * enchaîne la suivante s'il y a lieu. `handledRef` évite tout re-déclenchement pendant l'écriture
 * asynchrone (l'état serveur n'est pas encore rafraîchi) ou si celle-ci échoue.
 */
export function OnboardingHost() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const currentUser = useAuthStore((s) => s.currentUser);
  const updateOnboarding = useAuthStore((s) => s.updateOnboarding);
  const requested = useTourStore((s) => s.requested);
  const consumeRequest = useTourStore((s) => s.consume);
  const setRailOpen = useTourStore((s) => s.setRailOpen);

  const [personaOpen, setPersonaOpen] = useState(false);
  const [active, setActive] = useState<string | null>(null);
  // Visites déjà jouées dans cette session (au-delà de l'état serveur, pour ne pas reboucler).
  const handledRef = useRef<Set<string>>(new Set());

  const pending = (scope: string): boolean =>
    !!currentUser &&
    tourIsPending(scope, currentUser.onboardingState) &&
    !handledRef.current.has(scope);

  useEffect(() => {
    if (!currentUser || active) return;

    // 1. Rejeu explicite demandé par un bouton de section.
    if (requested) {
      consumeRequest();
      if (TOURS[requested]) setActive(requested);
      return;
    }

    // 2. Persona jamais choisi → modale (une fois).
    if (personaOpen) return;
    if (currentUser.persona === null) {
      setPersonaOpen(true);
      return;
    }

    // 3. Vue d'ensemble d'abord, 4. puis la visite de la section courante.
    if (pending('overview')) {
      setActive('overview');
      return;
    }
    const scope = scopeForPath(pathname);
    if (scope && pending(scope)) setActive(scope);
    // `pending`/`scopeForPath` sont dérivés de currentUser et pathname (deps réelles ci-dessous).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, pathname, requested, personaOpen, active]);

  // Ouvre le tiroir de navigation (mobile) tant qu'une visite ancrée sur le rail est active, pour
  // que ses coachmarks pointent des entrées visibles. Refermé dès la fin de la visite.
  useEffect(() => {
    const onRail = !!active && !!TOURS[active]?.anchorsRail;
    setRailOpen(onRail);
    return () => setRailOpen(false);
  }, [active, setRailOpen]);

  const applyPersona = (persona: UserPersona) => {
    setPersonaOpen(false);
    void updateOnboarding({ persona });
    // Effet doux : on ouvre l'onglet d'accueil du persona. Rien n'est masqué, la nav reste entière.
    const landing = PERSONA_LANDING[persona];
    if (landing !== '/') void navigate({ to: landing });
    // La visite `overview` (en attente au premier passage) démarrera via l'effet ci-dessus.
  };

  const finishActive = (scope: string, skipped: boolean) => {
    handledRef.current.add(scope);
    const def = TOURS[scope];
    if (def) void updateOnboarding({ markTourSeen: { scope, version: def.version, skipped } });
    setActive(null);
  };

  if (personaOpen) {
    return <PersonaModal onConfirm={applyPersona} onSkip={() => applyPersona('GENERALIST')} />;
  }

  const activeTour = active ? TOURS[active] : undefined;
  if (active && activeTour) {
    return (
      <GuidedTour
        key={active}
        tour={activeTour}
        onClose={(skipped) => finishActive(active, skipped)}
      />
    );
  }
  return null;
}
